import {
  ChangeEvent,
  memo,
  ReactNode,
  useCallback,
  useRef,
  useState,
} from "react";
import {
  IDictionary,
  PartialProduct,
  PaymentInfo,
} from "../../types/PartialProduct";
import InputMask from "../InputMask";
import { useNavigate, useOutletContext } from "react-router-dom";
import { StoreData } from "../../types/StoreData";
import { Country } from "country-state-city";
import Stripe from "stripe";
import { PayStripe } from "../../PayApi";
import {
  Elements,
  ExpressCheckoutElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import {
  StripeExpressCheckoutElementConfirmEvent,
  loadStripe,
  StripeElementsOptions,
  StripeExpressCheckoutElementClickEvent,
  Stripe as jsStripe,
} from "@stripe/stripe-js";
import useProductState from "../../hooks/UseProductState";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectValue,
  SelectTrigger,
  Input,
  Button,
  Label,
} from "@tides/base-library";
import { Spinner } from "../Spinner";

const cards = [
  "cc-visa",
  "cc-discover",
  "cc-mastercard",
  "cc-amex",
  "cc-jcb",
  "cc-diners-club",
  "cc-union-pay",
];

const allCountries = Object.assign(
  {},
  ...Country.getAllCountries().map((country) => ({
    [country.isoCode]: country.name,
  }))
) as IDictionary<string>;

interface Props {
  product: PartialProduct;
  children?: ReactNode;
  email?: string;
}

interface Country {
  value: string;
  text: string;
}

interface State {
  options: StripeElementsOptions;
  formattedTotal: string;
  loadStripe: Promise<jsStripe | null>;
  countries: Country[];
}

const StripeProduct = memo(function StripeProduct({
  product,
  children,
  email,
}: Props): ReactNode {
  const { alpha2, hasPostal } = useOutletContext<StoreData>();
  const {
    country,
    state,
    toggleProcessing,
    handleCardNameChange,
    handleCardNumberInput,
    handleCountrySelected,
    handleEmailChange,
    handleExpirationChange,
    handlePostalChange,
    handleCVCChange,
  } = useProductState(alpha2, hasPostal);
  const [productState] = useState<State>({
    loadStripe: loadStripe(product.accepted_payment_methods["stripe"].api_key!),
    options: {
      mode: "payment",
      paymentMethodCreation: "manual",
      amount: product.price,
      currency: "usd",
      appearance: { theme: "night" },
    },
    formattedTotal: new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "USD",
      currencyDisplay: "narrowSymbol",
    }).format(product.price / 100),
    countries: Object.keys(allCountries).map((countryCode) => {
      return { value: countryCode, text: allCountries[countryCode] };
    }),
  });

  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);

  const stripe = new Stripe(
    product.accepted_payment_methods["stripe"].api_key!,
    {
      // @ts-expect-error api version can be set null.
      apiVersion: null,
    }
  );

  const handleSelect = useCallback(function handleSelect(value: string) {
    handleCountrySelected(value);
  }, []);

  async function handleOnPay() {
    const target = formRef.current;
    if (!target) return;
    if (!target.reportValidity()) return;

    toggleProcessing();

    try {
      const pm = await stripe.paymentMethods.create({
        type: "card",
        card: {
          number: state.cardNumber!.replace(/\s/g, ""),
          exp_month: state.expiration!.getMonth() + 1,
          exp_year: state.expiration!.getFullYear(),
          cvc: state.cvc,
        },
        billing_details: {
          email: state.email,
          address: {
            country: country,
            postal_code: state.postalCode,
          },
          name: state.billingName,
        },
      });

      const paymentInfo = {
        confirm_url: `${window.location.origin}/done`,
        email: state.email!,
        user_name: state.billingName!,
        payment_method: pm.id,
      } as PaymentInfo;

      await payNow(paymentInfo);
    } catch (ex) {
      console.error(ex);

      navigate("/failed");
    }
  }

  async function payNow(info: PaymentInfo) {
    try {
      const result = await PayStripe(info, product.id);

      if (result) {
        if (result.needs_confirmation) {
          window.location.href = result.redirect_url;
        } else {
          const orderKey = result.order_key
            ? `&order_key=${result.order_key}`
            : "";
          navigate(
            `/success?order_id=${result.order_id}${orderKey}&email=${result.email}`,
            { replace: true }
          );
        }
      } else {
        navigate("/failed");
      }
    } catch (ex) {
      console.error(ex);

      navigate("/failed");
    }
  }

  const expressClick = useCallback(function expressClick(
    event: StripeExpressCheckoutElementClickEvent
  ) {
    const target = emailRef.current;
    if (!target) return;
    if (!target.reportValidity()) return;

    event.resolve({
      emailRequired: true,
    });
  },
  []);

  return (
    <>
      <form
        ref={formRef}
        className="grid-cols-1 mt-0 grid gap-4 rounded-md py-12 md:grid-cols-2"
        autoComplete="off"
      >
        <section className="col-span-2 flex h-fit flex-col gap-2.5">
          {children}
          <Elements
            stripe={productState.loadStripe}
            options={productState.options}
          >
            <StripeExpress
              onFailed={useCallback(() => navigate("/failed"), [])}
              onReady={payNow}
              onClick={expressClick}
              toggleProcessing={toggleProcessing}
            />
          </Elements>

          <div className="mt-6 flex flex-row items-center justify-center">
            <hr className="w-full border" />
            <p className="flex-shrink-0 gray-400 flex px-4 text-xs md:text-base md:leading-4">
              or pay with card
            </p>
            <hr className="w-full border" />
          </div>
        </section>

        <section className="flex flex-col gap-2 md:grid-cols-2 md:col-span-2 md:grid">
          <article className="col-span-2 h-full min-w-sm grid w-full items-center gap-1.5">
            <Label>Email</Label>
            <Input
              id="email"
              type="email"
              onChange={useCallback(
                (e: ChangeEvent<HTMLInputElement>) =>
                  handleEmailChange(e.target.value),
                []
              )}
              ref={emailRef}
              required={true}
              defaultValue={email}
            />
          </article>

          <article className="col-span-2 h-full min-w-sm grid w-full items-center gap-1.5">
            <Label>Name on Card</Label>
            <Input
              id="cardholder-name"
              onChange={handleCardNameChange}
              required={true}
              className="col-span-2"
            />
          </article>

          <InputMask
            mask="0000 0000 0000 0000"
            id="card_number"
            className="col-span-2"
            max_length={0}
            card={state.cardIcon}
            onInput={handleCardNumberInput}
            required={true}
            label="Card Number"
          />

          <article className="col-span-2 inline-flex gap-2">
            <InputMask
              mask="00/00"
              max_length={5}
              placeholder="MM/YY"
              label="Expiration"
              id="expiration"
              onChange={handleExpirationChange}
              required={true}
            />

            <InputMask
              mask="00000"
              max_length={5}
              id="cvc"
              label="CVC"
              onChange={handleCVCChange}
              required={true}
            />
          </article>

          <article className="min-w-sm col-span-2 grid w-full items-center gap-1.5">
            <Label>Country</Label>
            <Select value={country} onValueChange={handleSelect}>
              <SelectTrigger>
                <SelectValue placeholder="Crypto" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-700 text-white">
                {productState.countries.map((c) => (
                  <SelectItem value={c.value} key={c.value}>
                    {c.text}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </article>

          {state.hasPostal ? (
            <article className="col-span-2 min-w-sm grid w-full items-center gap-1.5">
              <Label>{state.postalPlaceholder}</Label>
              <Input
                className="col-span-2"
                onChange={handlePostalChange}
                required={true}
              />
            </article>
          ) : (
            <></>
          )}
        </section>

        {state.processing ? (
          <Spinner text="Loading..." />
        ) : (
          <section className="col-span-2 flex flex-col gap-2.5 self-end">
            <Button
              className="w-full text-white"
              type="button"
              onClick={handleOnPay}
            >
              Pay {productState.formattedTotal}
            </Button>
          </section>
        )}
      </form>

      <article className="flex justify-center">
        <section
          id="cards"
          className="grid-cols-4 mt-5 grid w-[272px] place-items-center gap-4"
        >
          {cards.map((value) => (
            <img
              key={value}
              height={35}
              width={35}
              src={`cards/${value}.svg`}
            />
          ))}
          <img height="35" width="35" src="cards/pci.svg" />
        </section>
      </article>
    </>
  );
});

interface ExpressProps {
  onFailed: () => void;
  onReady: (info: PaymentInfo) => void | Promise<void>;
  onClick: (event: StripeExpressCheckoutElementClickEvent) => void;
  toggleProcessing: () => void;
}

const StripeExpress = memo(function StripeExpress({
  onFailed,
  onReady,
  onClick,
  toggleProcessing,
}: ExpressProps): ReactNode {
  const elements = useElements();
  const stripe = useStripe();

  async function onElementConfrim(
    event: StripeExpressCheckoutElementConfirmEvent
  ) {
    if (!stripe || !elements) return;

    if (!event.billingDetails) {
      event.paymentFailed({ reason: "fail" });
      onFailed();

      return;
    }

    const { error } = await elements.submit();

    if (error) {
      toggleProcessing();
      return;
    }

    const emailElement = document.getElementById("email") as HTMLInputElement;

    try {
      const { paymentMethod } = await stripe.createPaymentMethod({
        elements,
      });

      toggleProcessing();

      await onReady({
        confirm_url: `${window.location.origin}/done`,
        email: emailElement.value,
        user_name: event.billingDetails.name,
        payment_method: paymentMethod!.id,
      } as PaymentInfo);
    } catch (ex) {
      console.error(ex);
      onFailed();
    }
  }

  return (
    <ExpressCheckoutElement
      options={{ layout: { overflow: "never" } }}
      onConfirm={onElementConfrim}
      onClick={onClick}
    />
  );
});

export default StripeProduct;
